import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/shared/PageHeader";
import ImagePreviewModal from "../components/shared/ImagePreviewModal";
import PageContainer from "../components/shared/PageContainer";
import StatusBadge from "../components/shared/StatusBadge";
import {
  Badge,
  Button,
  Pagination,
  Select,
  Spinner,
  TextInput,
  Td,
  Th,
  Tr,
} from "../components/ui";
import { formatDate } from "../utils/formatters";
import { ordersApi } from "../services/api";
import { Order } from "../types";
import { useOrdersTableRefresh } from "../hooks/useOrdersTableRefresh";

/* Filter-bar cells. The kit's inputs put `className` on the control itself, so
   flex sizing lives on a wrapper. Search gets twice the share and a larger
   minimum; "Rows" needs the least. */
const FILTER = "min-w-44 flex-1";
const FILTER_NARROW = "min-w-32 flex-none";
const FILTER_WIDE = "min-w-56 flex-[2]";

const toDateInputValue = (value?: string | Date | null): string => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
};

const formatPrice = (value?: number): string => {
  if (value == null) return "-";
  return `£ ${value}`;
};

const getFirstImage = (order: Order): string | null => {
  if (!order.formData) return null;
  try {
    const parsed = JSON.parse(order.formData);
    for (const key of ["picture1", "picture2", "image"]) {
      if (parsed[key] && typeof parsed[key] === "string" && parsed[key].startsWith("data:image")) {
        return parsed[key];
      }
    }
  } catch {}
  return null;
};

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateOrdered, setDateOrdered] = useState("");
  const [dateRequired, setDateRequired] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    return await ordersApi.getOrders();
  }, []);

  const { refreshOrders, isRefreshing } = useOrdersTableRefresh<Order>(
    fetchOrders,
    setOrders,
    setLoading,
    (error) => console.error("Failed to fetch orders:", error)
  );

  useEffect(() => {
    refreshOrders({ showLoading: true });
  }, [refreshOrders]);

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      !searchTerm ||
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.supplierName?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDateOrdered = true;
    if (dateOrdered && order.dateOrdered) {
      matchesDateOrdered = toDateInputValue(order.dateOrdered) === dateOrdered;
    }

    let matchesDateRequired = true;
    if (dateRequired && order.dateRequired) {
      matchesDateRequired =
        toDateInputValue(order.dateRequired) === dateRequired;
    }

    return matchesSearch && matchesDateOrdered && matchesDateRequired;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateOrdered, dateRequired, entriesPerPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOrders.length / entriesPerPage)
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * entriesPerPage;
  const displayedOrders = filteredOrders.slice(
    startIndex,
    startIndex + entriesPerPage
  );
  const startEntry = filteredOrders.length === 0 ? 0 : startIndex + 1;
  const endEntry =
    filteredOrders.length === 0
      ? 0
      : Math.min(startIndex + displayedOrders.length, filteredOrders.length);

  const columnCount = 10;

  return (
    <PageContainer>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader title="Manage Orders" subTitle="Orders" />

        <Button
          variant="outline"
          onClick={() => refreshOrders()}
          disabled={isRefreshing}
          className="shrink-0"
        >
          {isRefreshing ? (
            <>
              <Spinner size="size-4" />
              Refreshing…
            </>
          ) : (
            <>
              <i className="bi bi-arrow-clockwise" />
              Refresh
            </>
          )}
        </Button>
      </div>

      <section className="panel overflow-hidden">
        {/* One row of filters: each control flexes from a sensible minimum, so
            they sit on a single line on desktop and wrap only when narrow. */}
        <header className="border-hairline/60 flex flex-wrap items-end gap-3 border-b p-5">
          {/* Labels replace the old tooltips: a date field with no visible
              label is unreadable until hovered. */}
          <div className={FILTER}>
            <TextInput
              label="Date ordered"
              type="date"
              value={dateOrdered}
              onChange={(e) => setDateOrdered(e.target.value)}
            />
          </div>
          <div className={FILTER}>
            <TextInput
              label="Date required"
              type="date"
              value={dateRequired}
              onChange={(e) => setDateRequired(e.target.value)}
            />
          </div>
          <div className={FILTER_NARROW}>
            <Select
              label="Rows"
              value={entriesPerPage}
              onChange={(e) => setEntriesPerPage(Number(e.target.value))}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </Select>
          </div>
          <div className={FILTER_WIDE}>
            <TextInput
              label="Search"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Order, branch, supplier…"
            />
          </div>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Order</Th>
                <Th>Image</Th>
                <Th>Branch</Th>
                <Th>Supplier</Th>
                <Th>Date Ordered</Th>
                <Th>Date Required</Th>
                <Th>Supplier Price</Th>
                <Th>Branch Price</Th>
                <Th>Status</Th>
                <Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-12 text-center">
                    <span className="inline-flex items-center gap-2 text-sm text-zinc-500">
                      <Spinner size="size-4" />
                      Loading orders…
                    </span>
                  </td>
                </tr>
              ) : displayedOrders.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-4 py-12 text-center text-sm text-zinc-500"
                  >
                    No orders found.
                  </td>
                </tr>
              ) : (
                displayedOrders.map((order) => (
                  <Tr key={order.id}>
                    <Td className="font-medium text-zinc-900">
                      {order.orderNumber || "-"}
                    </Td>
                    <Td>
                      {getFirstImage(order) ? (
                        <button
                          type="button"
                          className="text-brand-strong hover:text-brand inline-flex items-center gap-1.5 text-sm transition-colors"
                          onClick={() => setPreviewImage(getFirstImage(order))}
                        >
                          <i className="bi bi-image" />
                          View
                        </button>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </Td>
                    <Td>{order.clientName || "-"}</Td>
                    <Td>{order.supplierName || "-"}</Td>
                    <Td className="text-zinc-500">
                      {formatDate(order.dateOrdered)}
                    </Td>
                    <Td className="text-zinc-500">
                      {formatDate(order.dateRequired)}
                    </Td>
                    <Td className="tabular-nums">
                      {formatPrice(order.supplierPrice)}
                    </Td>
                    <Td className="tabular-nums">
                      {formatPrice(order.clientPrice)}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={order.status || "Pending"} />
                        {order.isEdited && (
                          <Badge tone="brand">
                            <i className="bi bi-pencil-fill" />
                            Edited
                          </Badge>
                        )}
                      </div>
                    </Td>
                    <Td className="text-right">
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/orders/view/${order.id}`)}
                        className="px-3 py-1.5 text-xs"
                      >
                        <i className="bi bi-eye" />
                        View
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <footer className="border-hairline/60 flex flex-col items-center justify-between gap-3 border-t px-5 py-4 sm:flex-row">
          <p className="text-xs text-zinc-500">
            Showing rows {startEntry} to {endEntry} of {filteredOrders.length}
          </p>

          <Pagination
            page={safeCurrentPage}
            totalPages={totalPages}
            onChange={setCurrentPage}
          />
        </footer>
      </section>

      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />
    </PageContainer>
  );
};

export default Orders;
